import'string.prototype.startswith';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewTobaForm from "../../components/newTobaForm";
import {
    addTobaFormAttachment,
    changeNewTobaProperties,
    changeTobaSettings, closeCreateRevisionConfirmModal,
    closeTobaRevisionModal, closeUpdateTobaConfirmModal,
    getTobaRevisionById,
    openCreateRevisionConfirmModal,
    openTobaRevisionModal, openUpdateTobaConfirmModal,
    populateTobaFormFromRevision,
    removeFetchedTobaAttachment,
    removeTobaFormAttachment,
    requestPostNewToba,
    updateToba
} from "../../actions/toba";
import { goBack } from 'connected-react-router';
import { requestGetGroupsOfSolicitors } from "../../actions/groups";
import { getTobaById } from "../../actions/tobas";
// import download from 'in-browser-download';
import moment from "moment/moment";
import _ from 'lodash';
import { _getFileFromBase64 as getFileFromBase64Helper, fileToObjectHandle } from "../../helpers/functions";
import { TOBA_SHORT_TEXT, TOBA_PATH, LEGAL_ADVICE_TEXT } from '../../helpers/variables';
import CONTENT_STRINGS from '../../helpers/contentStrings';
import { MOMENT_DATE_FORMAT } from '../../helpers/constants';

const TobaFormHOC = (props) => {
    const { displayInModal = false, handleCancel } = props;

    const dispatch = useDispatch();

    const toba = useSelector(state => state.toba);
    const solicitorGroups = useSelector(state => state.groups.solicitorGroups.data);
    const routePathname = useSelector(state => state.router.location.pathname);

    const handleInputChange = useCallback((property, value) => dispatch(changeNewTobaProperties(property, value)), [dispatch]);
    const dispatchPostNewToba = useCallback((payload) => dispatch(requestPostNewToba(payload)), [dispatch]);
    const updateTobaSettings = useCallback((key, value) => dispatch(changeTobaSettings(key, value)), [dispatch]);
    const handleGoBack = useCallback(() => dispatch(goBack()), [dispatch]);
    const addAttachment = useCallback((attachement) => dispatch(addTobaFormAttachment(attachement)), [dispatch]);
    const removeAttachment = useCallback((key) => dispatch(removeTobaFormAttachment(key)), [dispatch]);
    const getSolicitorGroups = useCallback(() => dispatch(requestGetGroupsOfSolicitors()), [dispatch]);
    const fetchTobaById = useCallback((tobaId) => dispatch(getTobaById(tobaId)), [dispatch]);
    const removeFetchedAttachment = useCallback((tobaIndex) => dispatch(removeFetchedTobaAttachment(tobaIndex)), [dispatch]);
    const handlePopulateTobaFormFromRevision = useCallback((payload) => dispatch(populateTobaFormFromRevision(payload)), [dispatch]);
    const handleOpenTobaRevisionModal = useCallback((payload) => dispatch(openTobaRevisionModal(payload)), [dispatch]);
    const handleCloseTobaRevisionModal = useCallback((payload) => dispatch(closeTobaRevisionModal(payload)), [dispatch]);
    const dispatchUpdateToba = useCallback((payload) => dispatch(updateToba(payload)), [dispatch]);
    const fetchTobaRevisionById = useCallback((payload) => dispatch(getTobaRevisionById(payload)), [dispatch]);
    const handleOpenCreateRevisionConfirmModal = useCallback((payload) => dispatch(openCreateRevisionConfirmModal(payload)), [dispatch]);
    const handleCloseCreateRevisionConfirmModal = useCallback((payload) => dispatch(closeCreateRevisionConfirmModal(payload)), [dispatch]);
    const handleOpenUpdateTobaConfirmModal = useCallback((payload) => dispatch(openUpdateTobaConfirmModal(payload)), [dispatch]);
    const handleCloseUpdateTobaConfirmModal = useCallback((payload) => dispatch(closeUpdateTobaConfirmModal(payload)), [dispatch]);

    const editingForm = routePathname !== (`/solicitors/${TOBA_PATH}s`) && !displayInModal;

    // Fetch toba by id on mount when editing an existing record, then load solicitor groups
    useEffect(() => {
        if (editingForm) {
            const s = routePathname.split('/');
            fetchTobaById(s[3]);
        }
        getSolicitorGroups();
    }, []);

    // tracks whether a post was initiated in this session; the reducer clears successMsg on
    // REQUEST_POST_NEW_TOBA, so any truthy successMsg after this ref is set must be from the current post
    const hasStartedPostRef = useRef(false);

    useEffect(() => {
        if (toba.posting) hasStartedPostRef.current = true;
    }, [toba.posting]);

    // navigate back once the post completes with a success message, but only when editing
    useEffect(() => {
        if (hasStartedPostRef.current && editingForm && toba.successMsg) {
            hasStartedPostRef.current = false;
            handleGoBack();
        }
    }, [toba.successMsg, editingForm]);

    // ── Internal methods ─────────────────────────────────────────────────────

    const _validate = () => {
        const revisionsLoadingMsg = `${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.REVISIONS_LOADING}`;
        const editTobaMsg = `${CONTENT_STRINGS.EDIT} ${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.TO_CONTINUE}`;
        const contentEmptyMsg = `${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.CONTENT_MUST_NOT_BE_EMPTY}`;
        if (toba.fetchingTobaRevisions) {
            updateTobaSettings('failMsg', revisionsLoadingMsg);
            return false;
        }
        if (!toba.tobaFormData.name) {
            updateTobaSettings('failMsg', CONTENT_STRINGS.NAME_REQUIRED);
            return false;
        }
        if (!toba.tobaFormData.version) {
            updateTobaSettings('failMsg', CONTENT_STRINGS.VERSION_REQUIRED);
            return false;
        }
        if (!toba.tobaFormData.validFrom) {
            updateTobaSettings('failMsg', CONTENT_STRINGS.VALID_FROM_DATE_REQUIRED);
            return false;
        }
        if (!toba.changedMadeOnFetchedToba && editingForm) {
            updateTobaSettings('failMsg', editTobaMsg);
            return false;
        }
        if (!toba.tobaFormData.defaultToba && _.size(toba.tobaFormData.selectedSolicitorGroups) === 0) {
            updateTobaSettings('failMsg', `${CONTENT_STRINGS.SELECT} ${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.GROUPS} ${CONTENT_STRINGS.TO_CONTINUE}`);
            return false;
        }
        if (!toba.tobaFormData.tobaBody) {
            updateTobaSettings('failMsg', contentEmptyMsg);
            return false;
        }
        if (!moment(toba.tobaFormData.validFrom, MOMENT_DATE_FORMAT).isValid()) {
            updateTobaSettings('failMsg', CONTENT_STRINGS.VALID_FROM_DATE_INVALID);
            return false;
        }
        return true;
    };

    const _createToba = () => {
        const validF = moment(toba.tobaFormData.validFrom, MOMENT_DATE_FORMAT);
        dispatchPostNewToba({
            initialRevision: editingForm ? toba.tobaFormData.initialRevision : null,
            validFrom: validF.format(),
            content: toba.tobaFormData.tobaBody,
            name: toba.tobaFormData.name,
            version: toba.tobaFormData.version,
            tobaAttachmentDtos: [...toba.tobaFormData.attachments, ...toba.tobaFormData.fetchedTobaAttachments],
            solicitorGroupDtos: _.size(toba.tobaFormData.selectedSolicitorGroups) === 0
                ? null
                : _.map(toba.tobaFormData.selectedSolicitorGroups, g => ({ id: g.id })),
        });
    };

    const _patchToba = () => {
        const validF = moment(toba.tobaFormData.validFrom, MOMENT_DATE_FORMAT);
        dispatchUpdateToba({
            sendBody: {
                id: toba.tobaFormData.id,
                initialRevision: editingForm ? toba.tobaFormData.initialRevision : null,
                validFrom: validF.format(),
                content: toba.tobaFormData.tobaBody,
                name: toba.tobaFormData.name,
                version: toba.tobaFormData.version,
                tobaAttachmentDtos: [...toba.tobaFormData.attachments, ...toba.tobaFormData.fetchedTobaAttachments],
                solicitorGroupDtos: _.size(toba.tobaFormData.selectedSolicitorGroups) === 0
                    ? null
                    : _.map(toba.tobaFormData.selectedSolicitorGroups, g => ({ id: g.id })),
            },
            tobaId: toba.tobaFormData.id,
        });
    };

    const handleSubmit = (evt) => {
        evt.preventDefault();
        if (!_validate()) return false;
        if (toba.tobaFormData.id) {
            handleOpenCreateRevisionConfirmModal();
        } else {
            _createToba();
        }
    };

    const _handleUpdate = (evt) => {
        evt.preventDefault();
        if (!_validate()) return false;
        handleOpenUpdateTobaConfirmModal();
    };

    const _onDrop = (files) => {
        // tracks names already accepted in this drop, since toba.tobaFormData.attachments
        // won't include them yet (addAttachment is async) — without it, dropping two files
        // with the same name in one batch would add both instead of deduping
        const namesInThisBatch = [];
        for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
            let alreadyExists = false;
            for (let attachmentIndex = 0; attachmentIndex < toba.tobaFormData.attachments.length; attachmentIndex++) {
                if (files[fileIndex].name === toba.tobaFormData.attachments[attachmentIndex].name) {
                    alreadyExists = true;
                }
            }
            if (namesInThisBatch.includes(files[fileIndex].name)) {
                alreadyExists = true;
            }
            if (!alreadyExists) {
                namesInThisBatch.push(files[fileIndex].name);
                fileToObjectHandle(files[fileIndex])
                    .then(addAttachment)
                    .catch(error => console.log('Error: ', error));
            }
        }
    };

    const _getSelectGroupOptions = () => {
        let options = [];
        try {
            options = solicitorGroups.map(group => ({
                key: group.id,
                name: group.name,
                value: group,
            }));
            if (toba.tobaFormData.solicitorGroupFilter) {
                options = _.filter(options, o =>
                    _.lowerCase(o.name).includes(_.lowerCase(toba.tobaFormData.solicitorGroupFilter))
                );
            }
        } catch (e) {}
        return options;
    };

    const _getFileFromBase64 = (documentToDownload) => {
        getFileFromBase64Helper(documentToDownload);
    };

    const _getFutureTobaRevisions = () => {
        return _.filter(toba.previousTobas, pT =>
            moment(moment(pT.validFrom)).isAfter(moment(toba.tobaFormData.validFrom, MOMENT_DATE_FORMAT))
        );
    };

    const tobaIsLive = (tobaItem) => {
        if (tobaItem.validFrom && tobaItem.validUntil) {
            return moment().isBetween(tobaItem.validFrom, tobaItem.validUntil);
        }
        if (tobaItem.validFrom && !tobaItem.validUntil) {
            return moment().isAfter(tobaItem.validFrom);
        }
        return false;
    };


    const selectGroupOptions = useMemo(
        () => _getSelectGroupOptions(),
        [solicitorGroups, toba.tobaFormData.solicitorGroupFilter]
    );

    // ── Derived presentation values ──────────────────────────────────────────

    const formTitle = editingForm
        ? `${CONTENT_STRINGS.EDIT} ${TOBA_SHORT_TEXT}`
        : `${CONTENT_STRINGS.CREATE} ${TOBA_SHORT_TEXT}`;

    const updateModalMessageBase = `${CONTENT_STRINGS.ABOUT_TO_UPDATE_A} ${TOBA_SHORT_TEXT}.`;
    const updateModalAddendum = moment(moment(toba.tobaFormData.validFrom, MOMENT_DATE_FORMAT)).isBefore(moment())
        ? ` ${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.USERS_ACCEPTED_NOT_PROMPTED}`
        : '';
    const updateModalMessage = updateModalMessageBase + updateModalAddendum;

    const createRevisionMessage = `${CONTENT_STRINGS.ABOUT_TO_CREATE_REVISION_OF} ${TOBA_SHORT_TEXT}.  ${CONTENT_STRINGS.AFFECTED} ${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.USERS_ACCEPT_NEW} ${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.REVISION_WHEN_LIVE}`;
    const futureRevisionsWarning = `${CONTENT_STRINGS.ALL_REVISIONS_OF_THIS} ${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.STARTING_IN_FUTURE_ERASED}`;

    const solicitorGroupsLabel = `${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.GROUPS}`;
    const solicitorGroupsPlaceholder = `${CONTENT_STRINGS.QUICK_SEARCH_FOR} ${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.GROUPS}`;
    const selectedGroupsTitle = `${CONTENT_STRINGS.SELECTED} ${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.GROUPS}`;
    const revisionsTitle = `${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.REVISIONS}`;
    const revisionModalTitle = `${TOBA_SHORT_TEXT} ${CONTENT_STRINGS.REVISION}`;

    const validFromDate = toba.tobaFormData.validFrom
        ? moment(toba.tobaFormData.validFrom, MOMENT_DATE_FORMAT).toDate()
        : null;
    const minDate = moment().toDate();

    const showNameAndGroups = !(toba.tobaFormData.defaultToba && editingForm);
    const saveButtonLabel = editingForm
        ? CONTENT_STRINGS.SAVE
        : `${CONTENT_STRINGS.CREATE} ${TOBA_SHORT_TEXT}`;
    const cancelHandler = editingForm ? handleGoBack : handleCancel;
    const hasSelectedGroups = _.size(toba.tobaFormData.selectedSolicitorGroups) > 0;
    const hasExistingToba = !!toba.tobaFormData.id;
    const hasFetchedAttachments = editingForm && toba?.tobaFormData?.fetchedTobaAttachments?.length > 0;
    const futureTobaRevisionsExist = _getFutureTobaRevisions().length > 0;

    // ── Handlers passed to component ─────────────────────────────────────────

    const handleUpdateConfirm = () => {
        if (!_validate()) return false;
        handleCloseUpdateTobaConfirmModal();
        _patchToba();
    };

    const handleRevisionConfirm = () => {
        if (!_validate()) return false;
        handleCloseCreateRevisionConfirmModal();
        _createToba();
    };

    const handleFieldChange = (evt) => handleInputChange(evt.target.name, evt.target.value);
    const handleValidFromChange = (e) => {
        // datepicker passes null/invalid value when the field is cleared; skip writing it to form state
        const parsed = moment(e);
        if (parsed.isValid()) {
            handleInputChange('validFrom', parsed.format(MOMENT_DATE_FORMAT));
        }
    };
    const handleTobaBodyChange = (content) => handleInputChange('tobaBody', content);

    const selectManyOptions = selectGroupOptions.map(o => ({
        value: String(o.key),
        label: o.name,
    }));
    const selectManyValue = Object.values(toba.tobaFormData.selectedSolicitorGroups || {}).map(g => String(g.id));
    const handleSelectManyChange = (newValues) => {
        const newSelected = {};
        newValues.forEach(val => {
            const option = selectGroupOptions.find(o => String(o.key) === val);
            if (option) newSelected[option.key] = option.value;
        });
        handleInputChange('selectedSolicitorGroups', newSelected);
    };

    // ── List item builders ───────────────────────────────────────────────────

    const handleRevisionClick = useCallback((tobaId) => () => {
        handleOpenTobaRevisionModal();
        fetchTobaRevisionById({ tobaId });
    }, [handleOpenTobaRevisionModal, fetchTobaRevisionById]);

    const onAttachmentRemove = (attachment, index) => removeAttachment(index);
    const onFetchedAttachmentRemove = (attachment, index) => removeFetchedAttachment(index);
    const handleDownloadFile = (attachment) => _getFileFromBase64(attachment);



    const selectedGroupListItems = _.map(toba.tobaFormData.selectedSolicitorGroups, (item, index) => ({
        id: item.id,
        linkItemTestId: `create-toba-modal-selected-solicitor-groups-${index}`,
        content: item.name,
    }));

    return (
        <NewTobaForm
            toba={toba}
            goBack={handleGoBack}
            handleSubmit={handleSubmit}
            _handleUpdate={_handleUpdate}
            _onDrop={_onDrop}
            _getFileFromBase64={_getFileFromBase64}
            populateTobaFormFromRevision={handlePopulateTobaFormFromRevision}
            closeTobaRevisionModal={handleCloseTobaRevisionModal}
            closeUpdateTobaConfirmModal={handleCloseUpdateTobaConfirmModal}
            closeCreateRevisionConfirmModal={handleCloseCreateRevisionConfirmModal}
            formTitle={formTitle}
            updateModalMessage={updateModalMessage}
            createRevisionMessage={createRevisionMessage}
            futureRevisionsWarning={futureRevisionsWarning}
            solicitorGroupsLabel={solicitorGroupsLabel}
            solicitorGroupsPlaceholder={solicitorGroupsPlaceholder}
            selectedGroupsTitle={selectedGroupsTitle}
            revisionsTitle={revisionsTitle}
            revisionModalTitle={revisionModalTitle}
            validFromDate={validFromDate}
            minDate={minDate}
            showNameAndGroups={showNameAndGroups}
            saveButtonLabel={saveButtonLabel}
            cancelHandler={cancelHandler}
            hasSelectedGroups={hasSelectedGroups}
            hasExistingToba={hasExistingToba}
            hasFetchedAttachments={hasFetchedAttachments}
            futureTobaRevisionsExist={futureTobaRevisionsExist}
            selectManyOptions={selectManyOptions}
            selectManyValue={selectManyValue}
            handleUpdateConfirm={handleUpdateConfirm}
            handleRevisionConfirm={handleRevisionConfirm}
            handleFieldChange={handleFieldChange}
            handleValidFromChange={handleValidFromChange}
            handleTobaBodyChange={handleTobaBodyChange}
            handleSelectManyChange={handleSelectManyChange}
            selectedGroupListItems={selectedGroupListItems}
            attachments={toba.tobaFormData.attachments || []}
            onAttachmentRemove={onAttachmentRemove}
            handleDownloadFile={handleDownloadFile}
            onFetchedAttachmentRemove={onFetchedAttachmentRemove}
            fetchedAttachments={toba.tobaFormData.fetchedTobaAttachments || []}
            _getFutureTobaRevisions={_getFutureTobaRevisions}
            handleRevisionClick={handleRevisionClick}
            tobaIsLive={tobaIsLive}
       />
    );
};

export default TobaFormHOC;